import { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { useAuth } from "../../auth/AuthContext";
import { Button } from "../../ui/Button";
import { Input } from "../../ui/Input";

/**
 * Q · Wizard onboarding gérant.
 *
 * Affiché en overlay au premier login d'un GER si :
 *   - aucun site dans le tenant
 *   - ET pas de flag localStorage `victor_onboarding_skipped`
 *
 * 4 étapes : bienvenue → 1er site → 1er client → 1er agent.
 * Toutes skippables. Toute donnée saisie est créée en backend.
 */
export function OnboardingWizard() {
  const { user } = useAuth();
  const qc = useQueryClient();

  // Détermine si on doit afficher le wizard
  const sites = useQuery({
    queryKey: ["onboarding-sites"],
    queryFn: () => api.sites.list({}),
    enabled: Boolean(user),
  });

  const skipped =
    typeof window !== "undefined" &&
    window.localStorage.getItem("victor_onboarding_skipped") === "1";

  const shouldShow =
    user && sites.data && sites.data.length === 0 && !skipped;

  const [open, setOpen] = useState(shouldShow);
  const [step, setStep] = useState(0);

  // Forme : refresh quand sites.data devient connu
  if (shouldShow && !open) setOpen(true);

  const close = () => {
    setOpen(false);
    window.localStorage.setItem("victor_onboarding_skipped", "1");
  };

  const next = () => setStep((s) => s + 1);

  // Steps state
  const [siteForm, setSiteForm] = useState({
    nom: "",
    adresse: "",
    codePostal: "",
    ville: "",
  });
  const [clientForm, setClientForm] = useState({
    raisonSociale: "",
    siret: "",
    contactFacturationEmail: "",
  });
  const [agentForm, setAgentForm] = useState({
    prenom: "",
    nom: "",
    email: "",
  });

  const createSite = useMutation({
    mutationFn: () =>
      api.sites.create({
        nom: siteForm.nom.trim(),
        type: "gardiennage",
        adresse: siteForm.adresse.trim() || null,
        codePostal: siteForm.codePostal.trim() || null,
        ville: siteForm.ville.trim() || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["onboarding-sites"] });
      next();
    },
  });

  const createClient = useMutation({
    mutationFn: () =>
      api.clients.create({
        raisonSociale: clientForm.raisonSociale.trim(),
        siret: clientForm.siret.trim() || null,
        contactFacturationEmail:
          clientForm.contactFacturationEmail.trim() || null,
      }),
    onSuccess: () => next(),
  });

  const createAgent = useMutation({
    mutationFn: () =>
      api.agents.create({
        prenom: agentForm.prenom.trim(),
        nom: agentForm.nom.trim(),
        email: agentForm.email.trim() || null,
      }),
    onSuccess: () => next(),
  });

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy-950/60 px-4 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl"
      >
        <div className="flex items-center justify-between border-b border-navy-100 px-5 py-3">
          <span className="text-xs font-bold uppercase tracking-wider text-accent-500">
            Étape {Math.min(step + 1, 4)} / 4
          </span>
          <button
            type="button"
            onClick={close}
            className="text-xs text-navy-400 hover:text-navy-700"
          >
            Passer
          </button>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
            className="px-6 py-6"
          >
            {step === 0 ? (
              <div className="space-y-4 text-center">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-accent-50 text-accent-600">
                  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M9 12l2 2 4-4" />
                    <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9c2.39 0 4.68.94 6.36 2.64" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-navy-800">
                  Bienvenue {user?.prenom} !
                </h2>
                <p className="text-sm text-navy-600">
                  En 3 étapes, on configure ton premier site, ton premier
                  client et ton premier agent.
                </p>
                <Button onClick={next} className="w-full">
                  C'est parti
                </Button>
              </div>
            ) : null}

            {step === 1 ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  createSite.mutate();
                }}
                className="space-y-4"
              >
                <h2 className="text-xl font-bold text-navy-800">
                  Ton 1<sup>er</sup> site
                </h2>
                <p className="text-sm text-navy-500">
                  Le lieu où tes agents interviennent.
                </p>
                <Input
                  label="Nom du site *"
                  value={siteForm.nom}
                  onChange={(e) =>
                    setSiteForm({ ...siteForm, nom: e.target.value })
                  }
                  placeholder="ex. Carrefour Lille Centre"
                  required
                />
                <Input
                  label="Adresse"
                  value={siteForm.adresse}
                  onChange={(e) =>
                    setSiteForm({ ...siteForm, adresse: e.target.value })
                  }
                />
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    label="CP"
                    value={siteForm.codePostal}
                    onChange={(e) =>
                      setSiteForm({
                        ...siteForm,
                        codePostal: e.target.value,
                      })
                    }
                  />
                  <Input
                    label="Ville"
                    value={siteForm.ville}
                    onChange={(e) =>
                      setSiteForm({ ...siteForm, ville: e.target.value })
                    }
                  />
                </div>
                <div className="flex justify-between gap-2">
                  <Button type="button" variant="ghost" onClick={next}>
                    Passer
                  </Button>
                  <Button type="submit" disabled={createSite.isPending}>
                    {createSite.isPending ? "…" : "Créer & continuer"}
                  </Button>
                </div>
              </form>
            ) : null}

            {step === 2 ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  createClient.mutate();
                }}
                className="space-y-4"
              >
                <h2 className="text-xl font-bold text-navy-800">
                  Ton 1<sup>er</sup> client
                </h2>
                <p className="text-sm text-navy-500">
                  Le donneur d'ordre pour qui tu interviens.
                </p>
                <Input
                  label="Raison sociale *"
                  value={clientForm.raisonSociale}
                  onChange={(e) =>
                    setClientForm({
                      ...clientForm,
                      raisonSociale: e.target.value,
                    })
                  }
                  required
                />
                <Input
                  label="SIRET"
                  value={clientForm.siret}
                  onChange={(e) =>
                    setClientForm({ ...clientForm, siret: e.target.value })
                  }
                />
                <Input
                  label="Email facturation"
                  type="email"
                  value={clientForm.contactFacturationEmail}
                  onChange={(e) =>
                    setClientForm({
                      ...clientForm,
                      contactFacturationEmail: e.target.value,
                    })
                  }
                />
                <div className="flex justify-between gap-2">
                  <Button type="button" variant="ghost" onClick={next}>
                    Passer
                  </Button>
                  <Button type="submit" disabled={createClient.isPending}>
                    {createClient.isPending ? "…" : "Créer & continuer"}
                  </Button>
                </div>
              </form>
            ) : null}

            {step === 3 ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  createAgent.mutate();
                }}
                className="space-y-4"
              >
                <h2 className="text-xl font-bold text-navy-800">
                  Ton 1<sup>er</sup> agent
                </h2>
                <p className="text-sm text-navy-500">
                  Tu pourras le compléter et l'inviter plus tard.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    label="Prénom *"
                    value={agentForm.prenom}
                    onChange={(e) =>
                      setAgentForm({ ...agentForm, prenom: e.target.value })
                    }
                    required
                  />
                  <Input
                    label="Nom *"
                    value={agentForm.nom}
                    onChange={(e) =>
                      setAgentForm({ ...agentForm, nom: e.target.value })
                    }
                    required
                  />
                </div>
                <Input
                  label="Email"
                  type="email"
                  value={agentForm.email}
                  onChange={(e) =>
                    setAgentForm({ ...agentForm, email: e.target.value })
                  }
                />
                <div className="flex justify-between gap-2">
                  <Button type="button" variant="ghost" onClick={next}>
                    Passer
                  </Button>
                  <Button type="submit" disabled={createAgent.isPending}>
                    {createAgent.isPending ? "…" : "Créer & terminer"}
                  </Button>
                </div>
              </form>
            ) : null}

            {step >= 4 ? (
              <div className="space-y-4 text-center">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600">
                  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-navy-800">
                  Ton agence est prête
                </h2>
                <p className="text-sm text-navy-600">
                  Tu peux maintenant créer un contrat, planifier une
                  vacation, et commencer à utiliser Victor.
                </p>
                <Button onClick={close} className="w-full">
                  Voir mon tableau de bord
                </Button>
              </div>
            ) : null}
          </motion.div>
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
